import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "./useAuth";
import type { PermissionKey } from "@/types/permissions";
import type { Role } from "@/types/enums";

export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

// Оборачивает конкретный маршрут, требуя хотя бы одно из перечисленных прав.
// Если прав нет — пользователь видит понятное сообщение вместо белого экрана/404.
export function RequirePermission({ anyOf, children }: { anyOf: PermissionKey[]; children: ReactNode }) {
  const { hasAnyPermission } = useAuth();
  if (anyOf.length > 0 && !hasAnyPermission(anyOf)) {
    return (
      <Box p={4}>
        <Box component="p" fontSize={18} fontWeight={600}>
          Недостаточно прав для просмотра этого раздела
        </Box>
        <Box component="p" color="text.secondary">
          Обратитесь к директору или администратору, чтобы получить доступ.
        </Box>
      </Box>
    );
  }
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) {
    return <Box p={4}>Недостаточно прав для просмотра этого раздела</Box>;
  }
  return <>{children}</>;
}
